/**
 * Facebook Pixel tracking utilities for fbp and fbc parameters
 */

/* eslint-env browser */

/**
 * Helper function to extract fbc from URL fbclid parameter
 * Returns formatted fbc string (fb.1.{timestamp}.{fbclid}) or null if not found
 */
export const getFbcFromUrl = () => {
  try {
    if (typeof window !== 'undefined' && window.location && window.location.search) {
      const urlParams = new URLSearchParams(window.location.search);
      const fbclid = urlParams.get('fbclid');

      if (fbclid) {
        return 'fb.1.' + Math.floor(Date.now() / 1000) + '.' + fbclid;
      }
    }
    return null;
  } catch (error) {
    console.error('Facebook Pixel: Error extracting fbc from URL:', error);
    return null;
  }
};

/**
 * Helper function to get cookie value by name
 * Returns the cookie value if found, null if not found or on error
 */
const getCookie = cookieName => {
  try {
    // Check if document.cookie is available (browser environment)
    if (typeof document === 'undefined' || !document.cookie) {
      return null;
    }

    // Parse document.cookie to find the specified cookie
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();

      // Check if this cookie starts with "cookieName="
      if (cookie.indexOf(cookieName + '=') === 0) {
        const cookieValue = cookie.substring(cookieName.length + 1); // Remove "cookieName=" prefix

        if (cookieValue && cookieValue !== 'null' && cookieValue.trim() !== '') {
          return cookieValue;
        }
      }
    }

    return null;
  } catch (error) {
    console.error('Facebook Pixel: Error reading cookie:', cookieName, error);
    return null;
  }
};

/**
 * Gets Facebook Browser ID (fbp) from cookies and localStorage
 * Priority order: stored_fbp cookie -> stored_fbp localStorage -> _fbp cookie
 * Returns null if not found or if value is empty/"null"
 */
export const getFbp = () => {
  try {
    // First try stored_fbp cookie
    let fbp = getCookie('stored_fbp');
    if (fbp) {
      console.log('Facebook Pixel: Retrieved fbp from stored_fbp cookie:', fbp);
      return fbp;
    }

    // Second try stored_fbp from localStorage
    fbp = localStorage.getItem('stored_fbp');
    if (fbp && fbp !== 'null' && fbp.trim() !== '') {
      console.log('Facebook Pixel: Retrieved fbp from stored_fbp localStorage:', fbp);
      return fbp;
    }

    // Fallback to _fbp cookie
    fbp = getCookie('_fbp');
    if (fbp) {
      console.log('Facebook Pixel: Retrieved fbp from _fbp cookie:', fbp);
      return fbp;
    }

    console.log('Facebook Pixel: No valid fbp found');
    return null;
  } catch (error) {
    console.error('Facebook Pixel: Error retrieving fbp:', error);
    return null;
  }
};

/**
 * Gets Facebook Click ID (fbc) from URL parameters, cookies, and localStorage
 * Priority order: fbclid URL parameter -> stored_fbc cookie -> stored_fbc localStorage
 * Returns null if not found or if value is empty/"null"
 */
export const getFbc = () => {
  try {
    // First try to extract fbclid from URL parameters (highest priority)
    let fbc = getFbcFromUrl();
    if (fbc) {
      console.log('Facebook Pixel: Retrieved fbc from URL parameter (fbclid):', fbc);
      return fbc;
    }

    // Second try stored_fbc cookie
    fbc = getCookie('stored_fbc');
    if (fbc) {
      console.log('Facebook Pixel: Retrieved fbc from stored_fbc cookie:', fbc);
      return fbc;
    }

    // Third try stored_fbc from localStorage
    fbc = localStorage.getItem('stored_fbc');
    if (fbc && fbc !== 'null' && fbc.trim() !== '') {
      console.log('Facebook Pixel: Retrieved fbc from stored_fbc localStorage:', fbc);
      return fbc;
    }

    console.log('Facebook Pixel: No valid fbc found');
    return null;
  } catch (error) {
    console.error('Facebook Pixel: Error retrieving fbc:', error);
    return null;
  }
};

/**
 * Gets both fbp and fbc values for use in tracking
 * Returns an object with fbp and fbc properties (can be null)
 */
export const getFacebookPixelData = () => {
  return {
    fbp: getFbp(),
    fbc: getFbc(),
  };
};
